import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Activity } from 'lucide-react';
import { VENUE_TIMEZONE } from '@/lib/timezone';
/**
 * Classic-themed live activity feed.
 *
 * Shares its data shape with the Mission Control `MCDataStream`, but is styled
 * with the Classic slate/amber palette. The `mc/` components are hard-coded to
 * `mc-*` classes and CSS variables, so they cannot be reused here.
 */
/** Highlight window for newly-arrived rows. */
const FLASH_DURATION_MS = 1500;
const typeStyles = {
    BookingCreate: 'text-blue-300',
    SessionStart: 'text-blue-300',
    SessionEnd: 'text-purple-300',
    PaymentSettle: 'text-green-400',
    StatusChange: 'text-slate-400',
    QuickSale: 'text-amber-400',
    ReceiptUpload: 'text-amber-400',
    ReceiptReview: 'text-amber-400',
    OcrAnalyzer: 'text-amber-400',
    OcrHealth: 'text-green-400',
};
function formatTime(d) {
    return d.toLocaleTimeString('en-CA', {
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: false,
        timeZone: VENUE_TIMEZONE,
    });
}
export function ClassicDataStream({ events, maxEntries = 30, onOpenEvent, }) {
    const listRef = useRef(null);
    const capped = useMemo(() => events.slice(0, maxEntries), [events, maxEntries]);
    // Track seen ids so only genuinely new rows flash. The first render is marked
    // seen without animating, so the initial backlog stays calm.
    const seenIdsRef = useRef(null);
    const newIds = useMemo(() => {
        if (seenIdsRef.current === null)
            return new Set();
        const previous = seenIdsRef.current;
        const fresh = new Set();
        capped.forEach((ev) => {
            if (!previous.has(ev.id))
                fresh.add(ev.id);
        });
        return fresh;
    }, [capped]);
    useEffect(() => {
        seenIdsRef.current = new Set(capped.map((ev) => ev.id));
    }, [capped]);
    // Flash lifetime is kept separate from detection so the highlight survives
    // unrelated re-renders (the dashboard re-renders every second for its clock).
    const [flashingIds, setFlashingIds] = useState(() => new Set());
    const timersRef = useRef(new Map());
    useEffect(() => {
        if (newIds.size === 0)
            return;
        setFlashingIds((prev) => {
            const next = new Set(prev);
            newIds.forEach((id) => next.add(id));
            return next;
        });
        newIds.forEach((id) => {
            if (timersRef.current.has(id))
                return;
            const handle = window.setTimeout(() => {
                timersRef.current.delete(id);
                setFlashingIds((prev) => {
                    if (!prev.has(id))
                        return prev;
                    const next = new Set(prev);
                    next.delete(id);
                    return next;
                });
            }, FLASH_DURATION_MS);
            timersRef.current.set(id, handle);
        });
    }, [newIds]);
    useEffect(() => {
        const timers = timersRef.current;
        return () => {
            timers.forEach((handle) => window.clearTimeout(handle));
            timers.clear();
        };
    }, []);
    useEffect(() => {
        if (listRef.current)
            listRef.current.scrollTop = 0;
    }, [events.length]);
    return (_jsxs("div", { className: "flex flex-col h-full min-h-0", children: [_jsxs("div", { className: "flex items-center justify-between mb-3 shrink-0", children: [_jsxs("div", { className: "flex items-center gap-2", children: [_jsx(Activity, { className: "h-4 w-4 text-amber-400" }), _jsx("h3", { className: "text-sm font-semibold text-white", children: "Live Activity" })] }), _jsx("span", { className: "text-xs text-slate-500 font-mono", children: capped.length > 0 ? `${capped.length} events` : 'Real-Time' })] }), _jsx("div", { ref: listRef, className: "flex-1 min-h-0 overflow-y-auto rounded-lg bg-slate-900/50 border border-slate-700 p-2", children: capped.length === 0 ? (_jsx("div", { className: "text-xs text-slate-500 font-mono py-8 text-center", children: "Awaiting events\u2026" })) : (_jsx("ul", { className: "space-y-1", children: capped.map((ev) => {
                        const isFlashing = flashingIds.has(ev.id);
                        const isActionable = Boolean(ev.linkHref && onOpenEvent);
                        const content = (_jsxs(_Fragment, { children: [_jsxs("div", { className: "flex items-baseline gap-2", children: [_jsx("span", { className: "text-[11px] text-slate-500 font-mono shrink-0", children: formatTime(ev.timestamp) }), _jsx("span", { className: `text-xs font-semibold ${typeStyles[ev.type]}`, children: ev.type }), ev.secondary && (_jsx("span", { className: "text-xs text-white font-medium truncate", children: ev.secondary }))] }), _jsx("div", { className: "text-xs text-slate-400 mt-0.5 break-words", children: ev.primary })] }));
                        return (_jsx("li", { className: `rounded transition-colors duration-500 ${isFlashing ? 'bg-amber-500/10' : ''}`, children: isActionable ? (_jsx("button", { type: "button", onClick: () => onOpenEvent?.(ev), className: "w-full text-left px-2 py-1.5 rounded hover:bg-slate-700/50 focus:outline-none focus-visible:ring-1 focus-visible:ring-amber-400", "aria-label": `Open ${ev.type} event: ${ev.primary}`, children: content })) : (_jsx("div", { className: "px-2 py-1.5", children: content })) }, ev.id));
                    }) })) })] }));
}
export default ClassicDataStream;
